import { useEffect, useRef, useState } from 'react';

/**
 * Mirror of admin-ui useInViewport — single shared IO, slot/queue
 * management with active vs queued tracking, negative rootMargin so
 * cards release as soon as they scroll past the middle of the
 * viewport (not waiting to fully exit + 200px).
 */

interface Slot {
  setInView: (v: boolean) => void;
  active: boolean;
  queued: boolean;
  intersecting: boolean;
}

const ELEMENTS = new WeakMap<Element, Slot>();
const SLOTS = new Set<Slot>();
const QUEUE: Slot[] = [];

let currentCap = 12;
let currentRootMargin = '-25% 0% -25% 0%';
let sharedIO: IntersectionObserver | null = null;

function ensureObserver(): IntersectionObserver {
  if (sharedIO) return sharedIO;
  sharedIO = new IntersectionObserver((entries) => {
    for (const e of entries) {
      const slot = ELEMENTS.get(e.target);
      if (!slot) continue;
      slot.intersecting = e.isIntersecting;
      if (e.isIntersecting) acquire(slot);
      else release(slot);
    }
  }, { rootMargin: currentRootMargin });
  return sharedIO;
}

function acquire(slot: Slot): void {
  if (slot.active) return;
  if (SLOTS.size < currentCap) {
    slot.active = true;
    slot.queued = false;
    SLOTS.add(slot);
    slot.setInView(true);
  } else if (!slot.queued) {
    slot.queued = true;
    QUEUE.push(slot);
  }
}

function release(slot: Slot): void {
  if (slot.active) {
    slot.active = false;
    SLOTS.delete(slot);
    slot.setInView(false);
    promoteNext();
    return;
  }
  if (slot.queued) {
    const idx = QUEUE.indexOf(slot);
    if (idx !== -1) QUEUE.splice(idx, 1);
    slot.queued = false;
  }
}

function promoteNext(): void {
  while (QUEUE.length && SLOTS.size < currentCap) {
    const next = QUEUE.shift();
    if (!next) break;
    next.queued = false;
    if (next.intersecting) {
      next.active = true;
      SLOTS.add(next);
      next.setInView(true);
    }
  }
}

export function useInViewport(opts: { rootMargin?: string; cap?: number } = {}): {
  ref: React.RefObject<HTMLDivElement>;
  inView: boolean;
} {
  const { cap, rootMargin } = opts;
  if (typeof cap === 'number' && cap > 0 && cap !== currentCap) currentCap = cap;
  if (rootMargin && rootMargin !== currentRootMargin) {
    currentRootMargin = rootMargin;
    if (sharedIO) {
      sharedIO.disconnect();
      sharedIO = null;
    }
  }

  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }
    const slot: Slot = { setInView, active: false, queued: false, intersecting: false };
    ELEMENTS.set(el, slot);
    const io = ensureObserver();
    io.observe(el);

    return () => {
      io.unobserve(el);
      release(slot);
      ELEMENTS.delete(el);
    };
  }, []);

  return { ref, inView };
}
